// ProgramPage.js - Adam International Academy Documents Version
import './ProgramPage.css';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Import images
import heroImg from '../../assets/programs/hero.jpg';
import programOverviewImg from '../../assets/programs/overview.jpg';
import programGoalsImg from '../../assets/programs/goals.jpg';
import programStructureImg from '../../assets/programs/structure.jpg';
import internationalImg from '../../assets/programs/international.jpg';
import careersImg from '../../assets/programs/careers.jpg';

function ProgramPage() {
  const [activeTab, setActiveTab] = useState('elearning');
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Program categories from documents
  const programCategories = [
    {
      id: 'elearning',
      title: '📱 e-Learning Programs',
      description: 'Self-paced online courses with 24/7 access, interactive content, and SDG-aligned curriculum.',
      icon: '💻'
    },
    {
      id: 'blended',
      title: '🎯 Blended Learning',
      description: 'Hybrid programs combining online flexibility with face-to-face workshops and mentorship.',
      icon: '🔄'
    },
    {
      id: 'moocs',
      title: '🌍 MOOCs',
      description: 'Massive Open Online Courses with multilingual content and global participation.',
      icon: '🎓'
    },
    {
      id: 'diplomas',
      title: '📊 Professional Diplomas',
      description: '60-300 hour specialized diploma programs with industry-approved curriculum.',
      icon: '📜'
    },
    {
      id: 'webinars',
      title: '🔧 Technical Webinars',
      description: 'Live expert-led sessions with Q&A, sectoral updates, and recorded access.',
      icon: '🎥'
    },
    {
      id: 'masters',
      title: '🎓 Master\'s & PhD',
      description: 'Accredited postgraduate programs with partner universities worldwide.',
      icon: '🏛'
    }
  ];

  // Nine sectors from Adam Holding documents
  const adamSectors = [
    { name: "Commerce", icon: "🛍️", description: "Trade, retail, and international business" },
    { name: "Education", icon: "📚", description: "Academic programs and professional training" },
    { name: "Consultancy", icon: "💼", description: "Strategic, management, and technical consulting" },
    { name: "Industry", icon: "🏭", description: "Manufacturing, production, and industrial development" },
    { name: "Economy", icon: "📈", description: "Economic analysis, finance, and investment" },
    { name: "Construction", icon: "🏗️", description: "Infrastructure, building, and project management" },
    { name: "Technology", icon: "💻", description: "IT, software, AI, and digital transformation" },
    { name: "Services", icon: "🤝", description: "Professional and support services" },
    { name: "Sustainable Development", icon: "🌱", description: "SDG-aligned programs and ESG consulting" }
  ];

  // Program formats from documents
  const programFormats = [
    { name: "Self-Paced eLearning", duration: "Variable", certificate: "Digital Certificate" },
    { name: "Blended Learning Programs", duration: "3-6 months", certificate: "Professional Certificate" },
    { name: "Massive Open Online Courses", duration: "4-8 weeks", certificate: "Completion Badge" },
    { name: "Technical Webinars", duration: "2-6 hours", certificate: "Attendance Certificate" },
    { name: "Online Tutored Courses", duration: "8-12 weeks", certificate: "Course Certificate" },
    { name: "Mobile Learning", duration: "Micro-learning", certificate: "Module Badges" },
    { name: "Face-to-Face Training", duration: "1-4 weeks", certificate: "Training Certificate" },
    { name: "Master's Programs", duration: "1-2 years", certificate: "University Degree" },
    { name: "Professional Diplomas", duration: "60-300 hours", certificate: "Professional Diploma" }
  ];

  // Training categories from 2026 programs
  const trainingCategories = [
    {
      title: "🏛 Institutional & Organizational Development",
      programs: ["Risk Management", "Corporate Governance (GRC)", "Diplomacy & Public Affairs", 
                "Secretarial & Office Management", "Public Relations & Media", "Human Resources",
                "Women Empowerment (LEADWomen)", "Management & Leadership", "Customer Service",
                "Innovation & Creativity", "Strategy & Planning", "Marketing & Sales",
                "Quality & Productivity", "Personal Skills Development"]
    },
    {
      title: "💰 Financial Management",
      programs: ["Accounting & Auditing", "Investment & Cash Flows", "Finance & Budget Preparation"]
    },
    {
      title: "⚙️ Operations Management",
      programs: ["Project Management", "Procurement & Supply Chain", "Construction Management", 
                "Contracts & Tenders Management"]
    },
    {
      title: "🔧 Engineering & Applied Sciences",
      programs: ["Maintenance Management", "Facilities & Asset Management", "Urban Planning",
                "Renewable Energy", "Electrical Engineering", "Mechanical Engineering"]
    },
    {
      title: "🤖 Innovation & Technology",
      programs: ["Data Management & BI", "Renewable Energy", "Artificial Intelligence (AI)",
                "Digital Transformation", "Data Science & Cybersecurity"]
    },
    {
      title: "🛢️ Specialized Sectors",
      programs: ["Oil & Gas", "Renewable Energy", "HSE (Health, Safety & Environment)"]
    }
  ];

  // Certificate types from documents
  const certificateTypes = [
    { name: "Adam Center Professional Certificates", level: "Professional", validity: "Lifetime" },
    { name: "HRCI Approved Courses", level: "International", validity: "3 years" },
    { name: "University Accredited Degrees", level: "Academic", validity: "Lifetime" },
    { name: "Professional Diplomas", level: "Specialist", validity: "Lifetime" },
    { name: "Attendance Certificates", level: "Workshop", validity: "Course based" }
  ];

  return (
    <div className="pp-wrapper">

      {/* HERO - Updated with academy name */}
      <section
        className="pp-hero"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="pp-hero-overlay">
          <div className="pp-hero-content">
            <h1>📚 Academic & Professional Programs</h1>
            <p>
              Adam International Academy offers comprehensive capacity development programs across 9 sectors,
              delivered through 8 innovative learning formats. Aligned with SDG 2030 and accredited by 
              international partners.
            </p>
          </div>
        </div>
      </section>

      {/* Program Categories - New from documents */}
      <section 
        className="pp-categories"
        ref={el => sectionRefs.current.categories = el}
        data-section="categories"
      >
        <div className={`pp-container ${visibleSections.categories ? 'visible' : ''}`}>
          <h2 className="pp-section-title">🎯 Program Categories</h2>
          <div className="pp-categories-grid">
            {programCategories.map((category, index) => (
              <div key={index} className="pp-category-card">
                <div className="pp-category-icon">{category.icon}</div>
                <h3>{category.title}</h3>
                <p>{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adam 9 Sectors - From documents */}
      <section 
        className="pp-sectors"
        ref={el => sectionRefs.current.sectors = el}
        data-section="sectors"
      >
        <div className={`pp-container ${visibleSections.sectors ? 'visible' : ''}`}>
          <h2 className="pp-section-title">🏢 Adam 9 Sectors</h2>
          <p className="pp-section-subtitle">Our programs cover all nine sectors of Adam Holding Union</p>
          <div className="pp-sectors-grid">
            {adamSectors.map((sector, index) => (
              <div key={index} className="pp-sector-card">
                <div className="pp-sector-icon">{sector.icon}</div>
                <h3>{sector.name}</h3>
                <p>{sector.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Formats - From documents */}
      <section 
        className="pp-formats"
        ref={el => sectionRefs.current.formats = el}
        data-section="formats"
      >
        <div className={`pp-container ${visibleSections.formats ? 'visible' : ''}`}>
          <h2 className="pp-section-title">📋 Program Formats & Certificates</h2>
          <div className="pp-formats-table">
            <div className="pp-formats-header">
              <div className="pp-format-cell">Program Type</div>
              <div className="pp-format-cell">Duration</div>
              <div className="pp-format-cell">Certificate</div>
            </div>
            {programFormats.map((format, index) => (
              <div key={index} className="pp-formats-row">
                <div className="pp-format-cell">{format.name}</div>
                <div className="pp-format-cell">{format.duration}</div>
                <div className="pp-format-cell">{format.certificate}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Categories - From 2026 programs document */}
      <section 
        className="pp-training"
        ref={el => sectionRefs.current.training = el}
        data-section="training"
      >
        <div className={`pp-container ${visibleSections.training ? 'visible' : ''}`}>
          <h2 className="pp-section-title">📚 Training Programs 2026</h2>
          <div className="pp-training-grid">
            {trainingCategories.map((category, idx) => (
              <div key={idx} className="pp-training-category">
                <h3>{category.title}</h3>
                <div className="pp-training-programs">
                  {category.programs.map((program, index) => (
                    <span key={index} className="pp-training-tag">{program}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Introduction - Updated */}
      <section 
        className="pp-section"
        ref={el => sectionRefs.current.intro = el}
        data-section="intro"
      >
        <div className={`pp-container pp-section-content ${visibleSections.intro ? 'visible' : ''}`}>
          <div className="pp-section-text">
            <h2 className="pp-section-title">📚 Program Overview</h2>
            <p>
              Adam International Academy's capacity development programs combine theoretical knowledge with 
              practical application skills, preparing professionals for leadership roles in business, 
              government, and non-governmental organizations across all nine sectors.
            </p>
            <p>
              With flexible learning options including fully online, blended, and face-to-face formats, 
              our programs are designed with the needs of working professionals in mind. All programs 
              are aligned with the Sustainable Development Goals (SDG 2030) and peer-reviewed by 
              international experts.
            </p>
          </div>
          <img src={programOverviewImg} alt="Program Overview" className="pp-section-image" loading="lazy" />
        </div>
      </section>

      {/* Program Goals - Updated */}
      <section 
        className="pp-section"
        ref={el => sectionRefs.current.goals = el}
        data-section="goals"
      >
        <div className={`pp-container pp-section-content reverse ${visibleSections.goals ? 'visible' : ''}`}>
          <div className="pp-section-text">
            <h2 className="pp-section-title">🎯 Program Goals</h2>
            <ul className="pp-feature-list">
              <li>Strengthen technical capacities to contribute to SDG 2030 Agenda</li>
              <li>Develop expertise and promote innovation across all sectors</li>
              <li>Provide accessible, multilingual education for global professionals</li>
              <li>Foster strategic partnerships for collaborative capacity development</li>
              <li>Create enabling environment for sustainable economic advancement</li>
              <li>Embed competencies in local institutions with country-specific needs</li>
            </ul>
          </div>
          <img src={programGoalsImg} alt="Program Goals" className="pp-section-image" loading="lazy" />
        </div>
      </section>

      {/* Program Summary - Updated with comprehensive data */}
      <section 
        className="pp-section"
        ref={el => sectionRefs.current.summary = el}
        data-section="summary"
      >
        <div className={`pp-container ${visibleSections.summary ? 'visible' : ''}`}>
          <h2 className="pp-section-title">📌 Program Summary</h2>
          <div className="pp-summary-grid">
            <div className="pp-summary-card">
              <div className="pp-summary-icon">📊</div>
              <h3>Programs Offered</h3>
              <p>45+ programs across 9 sectors</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">⏱️</div>
              <h3>Duration Range</h3>
              <p>6 hours to 2 years</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">💻</div>
              <h3>Learning Formats</h3>
              <p>8 flexible formats</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">📅</div>
              <h3>Start Dates</h3>
              <p>Year-round enrollment</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">🎓</div>
              <h3>Certificate Types</h3>
              <p>Professional & Academic</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">💰</div>
              <h3>Tuition Range</h3>
              <p>Varies by program</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">🌐</div>
              <h3>Languages</h3>
              <p>English, Arabic, Turkish</p>
            </div>
            <div className="pp-summary-card">
              <div className="pp-summary-icon">🤝</div>
              <h3>Partner Universities</h3>
              <p>5+ international partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certificate Types - New section */}
      <section 
        className="pp-certificates"
        ref={el => sectionRefs.current.certificates = el}
        data-section="certificates"
      >
        <div className={`pp-container ${visibleSections.certificates ? 'visible' : ''}`}>
          <h2 className="pp-section-title">🏅 Certificate & Accreditation</h2>
          <div className="pp-certificates-grid">
            {certificateTypes.map((cert, index) => (
              <div key={index} className="pp-certificate-card">
                <div className="pp-certificate-icon">
                  {index === 0 ? "📜" : index === 1 ? "🌍" : index === 2 ? "🎓" : index === 3 ? "📋" : "✅"}
                </div>
                <h3>{cert.name}</h3>
                <p><strong>Level:</strong> {cert.level}</p>
                <p><strong>Validity:</strong> {cert.validity}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* International Experience - Updated with partner countries */}
      <section 
        className="pp-section"
        ref={el => sectionRefs.current.international = el}
        data-section="international"
      >
        <div className={`pp-container pp-section-content ${visibleSections.international ? 'visible' : ''}`}>
          <div className="pp-section-text">
            <h2 className="pp-section-title">🌍 International Experience</h2>
            <p>Students can participate in programs at our international locations:</p>
            <ul className="pp-feature-list">
              <li><strong>Turkey (Istanbul):</strong> Main campus with Medipol and Sabahattin Zaim universities</li>
              <li><strong>Bahrain (Manama):</strong> Adam Center headquarters for Middle East operations</li>
              <li><strong>Africa:</strong> Development programs in Algeria and across the continent</li>
              <li><strong>Europe & America:</strong> Exchange programs with partner institutions</li>
            </ul>
            <p>
              These international opportunities offer students the chance to observe leadership practices in 
              different cultures, build a global network, and gain cross-cultural competencies essential for 
              today's interconnected world.
            </p>
          </div>
          <img src={internationalImg} alt="International Experience" className="pp-section-image" loading="lazy" />
        </div>
      </section>

      {/* Career Fields - Updated from documents */}
      <section 
        className="pp-careers"
        ref={el => sectionRefs.current.careers = el}
        data-section="careers"
      >
        <div className={`pp-container ${visibleSections.careers ? 'visible' : ''}`}>
          <h2 className="pp-section-title">💼 Career Opportunities</h2>
          <div className="pp-careers-grid">
            <div className="pp-career-card">
              <h3>Policy Maker</h3>
              <p>Government and public sector leadership</p>
            </div>
            <div className="pp-career-card">
              <h3>Project Manager</h3>
              <p>International development and corporate projects</p>
            </div>
            <div className="pp-career-card">
              <h3>Development Consultant</h3>
              <p>SDG-aligned consulting for organizations</p>
            </div>
            <div className="pp-career-card">
              <h3>Program Designer</h3>
              <p>Capacity development program creation</p>
            </div>
            <div className="pp-career-card">
              <h3>Academic & Trainer</h3>
              <p>Teaching and professional training</p>
            </div>
            <div className="pp-career-card">
              <h3>Technical Specialist</h3>
              <p>Expert roles in engineering, IT, and applied sciences</p>
            </div>
            <div className="pp-career-card">
              <h3>Business Leader</h3>
              <p>Executive positions across all sectors</p>
            </div>
            <div className="pp-career-card">
              <h3>Sustainability Officer</h3>
              <p>Corporate sustainability and ESG roles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Graduate Statistics - Updated */}
      <section 
        className="pp-section"
        ref={el => sectionRefs.current.stats = el}
        data-section="stats"
      >
        <div className={`pp-container ${visibleSections.stats ? 'visible' : ''}`}>
          <h2 className="pp-section-title">📊 Graduate Statistics</h2>
          <div className="pp-stats-content">
            <img src={careersImg} alt="Career Statistics" className="pp-stats-image" loading="lazy" />
            <div className="pp-stats-text">
              <div className="pp-stat-item">
                <h3>2,500+</h3>
                <p>Global alumni network</p>
              </div>
              <div className="pp-stat-item">
                <h3>30+</h3>
                <p>Countries represented</p>
              </div>
              <div className="pp-stat-item">
                <h3>95%</h3>
                <p>Employment rate within 6 months</p>
              </div>
              <div className="pp-stat-item">
                <h3>100+</h3>
                <p>Faculty members worldwide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Categories - From documents */}
      <section 
        className="pp-premium"
        ref={el => sectionRefs.current.premium = el}
        data-section="premium"
      >
        <div className={`pp-container ${visibleSections.premium ? 'visible' : ''}`}>
          <h2 className="pp-section-title">⭐ Premium Program Categories</h2>
          <div className="pp-premium-grid">
            <div className="pp-premium-item">Basic Skills</div>
            <div className="pp-premium-item">Advanced Skills</div>
            <div className="pp-premium-item">Mini MBA Series</div>
            <div className="pp-premium-item">Corporate Sustainability</div>
            <div className="pp-premium-item">Emotional Intelligence</div>
            <div className="pp-premium-item">Institutional Flexibility</div>
          </div>
        </div>
      </section>

      {/* Application Call-to-Action - Updated */}
      <section 
        className="pp-apply-cta"
        ref={el => sectionRefs.current.apply = el}
        data-section="apply"
      >
        <div className={`pp-container ${visibleSections.apply ? 'visible' : ''}`}>
          <h2>Ready to Advance Your Career?</h2>
          <p>Join our global community of professionals and leaders</p>
          <div className="pp-apply-buttons">
            <Link to="/apply" className="pp-btn primary">Apply Now</Link>
            <Link to="/contact" className="pp-btn secondary">Contact Admissions</Link>
          </div>
          <div className="pp-contact-info">
            <p>📞 (+90) 380-772631</p>
            <p>✉️ info@adaminternationalacademy.com</p>
            <p>🌍 www.adaminternationalacademy.com</p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default ProgramPage;